<!--
  The page's breadcrumbs. The leftmost collapse into a popover as space runs out, then all of them
  once only one would fit. A native popover rather than SDropdownMenu keeps them links in the nav.
-->
<template>
  <Head :title="title" />
  <nav
    v-if="breadcrumbs.length"
    ref="nav"
    :aria-label="t('labels.breadcrumbs')"
    role="navigation"
    class="relative flex shrink min-w-0 w-full overflow-hidden"
  >
    <ol
      class="text-sm font-medium leading-tight flex shrink min-w-0"
      :class="{'flex-1': isCompact}"
    >
      <li
        v-if="popoverBreadcrumbs.length"
        class="group flex relative"
        :class="isCompact ? 'flex-1 min-w-0' : 'shrink-0'"
        @focusout="hidePopoverWhenFocusLeaves"
      >
        <button
          ref="popoverTrigger"
          type="button"
          :popovertarget="popoverId"
          class="flex items-center link-subtle outline-transparent focus-visible:outline-blue-400 -outline-offset-2"
          :class="
            isCompact
              ? 'flex-1 justify-between gap-1 min-w-0 pl-4 pr-3 py-1.5'
              : overflowTriggerClasses
          "
        >
          <template v-if="isCompact">
            <span class="truncate">
              <s-latex :content="parentBreadcrumb.label" :responsive="true" />
            </span>
            <s-icon
              name="chevron-down"
              class="text-gray-500 transition-transform duration-150 -my-2 shrink-0 group-has-[:popover-open]:rotate-180"
              size="1.25rem"
              aria-hidden="true"
            />
          </template>
          <template v-else>
            <s-icon name="dots-vertical" size="18" aria-hidden="true" />
            <span class="sr-only">{{ t('actions.showHiddenBreadcrumbs') }}</span>
          </template>
        </button>
        <ol
          :id="popoverId"
          ref="popover"
          popover="auto"
          class="bg-white text-blue-600 overflow-y-auto rounded-md shadow-lg ring-1 ring-black/10 divide-y divide-gray-200"
          :style="floatingStyles"
        >
          <li v-for="breadcrumb in popoverBreadcrumbs" :key="breadcrumb.href" class="flex">
            <span
              v-if="isCurrentPath(breadcrumb)"
              aria-current="page"
              :class="popoverBreadcrumbClasses"
              class="bg-gray-100 text-gray-800"
            >
              <s-latex :content="breadcrumb.label" :responsive="true" />
            </span>
            <s-link
              v-else
              :href="breadcrumb.href"
              :class="popoverBreadcrumbClasses"
              class="hover:bg-blue-50 focus:bg-blue-50 outline-transparent focus-visible:outline-blue-400 -outline-offset-2"
              @click="popover?.hidePopover()"
            >
              <s-latex :content="breadcrumb.label" :responsive="true" />
            </s-link>
          </li>
        </ol>
        <breadcrumb-separator v-if="!isCompact" />
      </li>
      <li
        v-for="breadcrumb in visibleBreadcrumbs"
        :key="breadcrumb.href"
        class="flex relative whitespace-nowrap min-w-0 max-w-full"
      >
        <span
          v-if="isCurrentPath(breadcrumb)"
          aria-current="page"
          :class="[breadcrumbClasses, currentBreadcrumbClasses]"
          class="text-gray-800"
        >
          <span class="truncate"><s-latex :content="breadcrumb.label" :responsive="true" /></span>
        </span>
        <template v-else>
          <s-link
            :class="breadcrumbClasses"
            class="link-subtle outline-transparent focus-visible:outline-blue-400 -outline-offset-2"
            :href="breadcrumb.href"
          >
            <span class="truncate"><s-latex :content="breadcrumb.label" :responsive="true" /></span>
          </s-link>
          <breadcrumb-separator />
        </template>
      </li>
    </ol>
    <ol
      ref="measurements"
      class="invisible absolute top-0 left-0 flex text-sm font-medium leading-tight whitespace-nowrap pointer-events-none"
      aria-hidden="true"
      inert
    >
      <li :class="overflowTriggerClasses">
        <s-icon name="dots-vertical" size="18" />
      </li>
      <li
        v-for="breadcrumb in breadcrumbs"
        :key="breadcrumb.href"
        :class="[breadcrumbClasses, isCurrentPath(breadcrumb) && currentBreadcrumbClasses]"
      >
        <s-latex :content="breadcrumb.label" :responsive="true" />
      </li>
    </ol>
  </nav>
</template>
<script setup lang="ts">
import SLink from '../components/SLink.vue';
import SLatex from '../components/SLatex.vue';
import SIcon from './SIcon.vue';
import BreadcrumbSeparator from './SBreadcrumbs/BreadcrumbSeparator.vue';
import {useI18n} from 'vue-i18n';
import {Head, usePage} from '@inertiajs/vue3';
import {computed, nextTick, ref, useId, watch} from 'vue';
import {useResizeObserver} from '@vueuse/core';
import {autoUpdate, offset, shift, size, useFloating} from '@floating-ui/vue';
import {useBreadcrumbTitle} from '../composables/useBreadcrumbTitle';
import BreadcrumbData = App.DTOs.BreadcrumbData;

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'local',
});

const page = usePage();

const breadcrumbClasses = 'flex items-center max-w-full pl-4 pr-3 py-1.5';
const currentBreadcrumbClasses = 'tracking-slight';
const overflowTriggerClasses = 'pl-3 pr-2 py-1.5';
const popoverBreadcrumbClasses = 'flex w-full items-center px-3 py-2';

function isCurrentPath(breadcrumb: BreadcrumbData) {
  const currentPath = window.location.pathname;
  return breadcrumb.href === currentPath || breadcrumb.href === currentPath + '/';
}

const breadcrumbs = computed<BreadcrumbData[]>(() => {
  return page.props.breadcrumbs || [];
});

const nav = ref<HTMLElement | null>(null);
const measurements = ref<HTMLElement | null>(null);
const availableWidth = ref(0);
const overflowTriggerWidth = ref(0);
const breadcrumbWidths = ref<number[]>([]);

function measure() {
  if (!nav.value || !measurements.value) return;

  const [overflowTrigger, ...breadcrumbElements] = Array.from(measurements.value.children);
  availableWidth.value = nav.value.clientWidth;
  overflowTriggerWidth.value = overflowTrigger.getBoundingClientRect().width;
  breadcrumbWidths.value = breadcrumbElements.map(
    (element) => element.getBoundingClientRect().width
  );
}

useResizeObserver(nav, measure);
useResizeObserver(measurements, measure);
watch(breadcrumbs, measure, {flush: 'post'});

const visibleCount = computed(() => {
  const widths = breadcrumbWidths.value;
  const totalWidth = widths.reduce((sum, width) => sum + width, 0);
  if (totalWidth <= availableWidth.value) return breadcrumbs.value.length;

  let usedWidth = overflowTriggerWidth.value;
  let count = 0;
  for (const width of [...widths].reverse()) {
    if (usedWidth + width > availableWidth.value) break;
    usedWidth += width;
    count++;
  }
  return Math.max(count, 1);
});

const isCompact = computed(() => breadcrumbs.value.length > 1 && visibleCount.value <= 1);
const parentBreadcrumb = computed(() => breadcrumbs.value[breadcrumbs.value.length - 2]);
const collapsedCount = computed(() =>
  isCompact.value ? breadcrumbs.value.length : breadcrumbs.value.length - visibleCount.value
);
const popoverBreadcrumbs = computed(() => breadcrumbs.value.slice(0, collapsedCount.value));
const visibleBreadcrumbs = computed(() => breadcrumbs.value.slice(collapsedCount.value));

const popoverId = useId();
const popoverTrigger = ref<HTMLElement | null>(null);
const popover = ref<HTMLElement | null>(null);

const {floatingStyles} = useFloating(popoverTrigger, popover, {
  strategy: 'fixed',
  placement: 'bottom-start',
  middleware: [
    offset(8),
    shift({padding: 8}),
    size({
      padding: 8,
      apply({availableWidth, availableHeight, elements, rects}) {
        Object.assign(elements.floating.style, {
          minWidth: isCompact.value ? `${Math.min(rects.reference.width, availableWidth)}px` : '',
          maxWidth: `${Math.max(0, availableWidth)}px`,
          maxHeight: `${Math.max(0, availableHeight)}px`,
        });
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
});

function hidePopoverWhenFocusLeaves(event: FocusEvent) {
  const focusTarget = event.relatedTarget as Node | null;
  if (focusTarget && !(event.currentTarget as HTMLElement).contains(focusTarget)) {
    popover.value?.hidePopover();
  }
}

watch(
  visibleCount,
  async () => {
    const breadcrumbsHadFocus = nav.value?.contains(document.activeElement);
    await nextTick();
    if (breadcrumbsHadFocus && !nav.value?.contains(document.activeElement)) {
      popoverTrigger.value?.focus();
    }
  },
  {flush: 'pre'}
);

const title = useBreadcrumbTitle();
</script>
<i18n>
{
  "en": {
    "labels.breadcrumbs": "Breadcrumbs",
    "actions.showHiddenBreadcrumbs": "Show hidden breadcrumbs"
  },
  "fr": {
    "labels.breadcrumbs": "Fil d'Ariane",
    "actions.showHiddenBreadcrumbs": "Afficher les éléments masqués du fil d'Ariane"
  }
}
</i18n>
